import React, { useState, useEffect, useMemo } from 'react';
import { Card, Form, Button } from 'react-bootstrap';
import { ReceptionLoadingBlock, ReceptionEmptyState } from '../ui';
import CompanyService from '../../../services/companyService';
import MemberService from '../../../services/memberService';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import HouseholdRegistrationModal from '../forms/HouseholdRegistrationModal';
import MemberListRows from './MemberListRows';
import BrowseSearchBar from '../browse/BrowseSearchBar';
import ReceptionMemberDetailModal from './ReceptionMemberDetailModal';
import ReceptionMemberEditModal from './ReceptionMemberEditModal';

export default function ReceptionMembersBrowse({ toast, memberTypeLabels }) {
  const ITEMS_PAGE = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 320);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [showHouseholdModal, setShowHouseholdModal] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [detailMember, setDetailMember] = useState(null);
  const [editMember, setEditMember] = useState(null);

  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === String(companyFilter)),
    [companies, companyFilter]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        if (!cancelled && res.success) setCompanies(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) toast('warning', `Sociétés (filtre) : ${e.message || 'erreur'}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, companyFilter]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const offset = page * ITEMS_PAGE;
        const res = await MemberService.getAllMembers(
          {
            limit: ITEMS_PAGE,
            offset,
            active_only: true,
            search_term: debouncedSearch.trim() || undefined,
            company_id: companyFilter !== 'all' ? parseInt(companyFilter, 10) : undefined
          },
          { signal: controller.signal }
        );
        if (cancelled) return;
        if (res.success) {
          setMembers(res.data || []);
          const tp = res.pagination?.totalPages ?? 1;
          const tm = res.pagination?.total ?? 0;
          setTotalPages(Math.max(1, tp));
          setTotalMembers(tm);
        }
      } catch (e) {
        if (!cancelled && e?.name !== 'AbortError')
          toast('danger', e.message || 'Impossible de charger les adhérents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [toast, debouncedSearch, companyFilter, page, reloadNonce]);

  const handleMemberUpdated = (updated) => {
    if (!updated?.id) {
      setReloadNonce((n) => n + 1);
      return;
    }
    setMembers((prev) =>
      prev.map((row) =>
        row.id === updated.id
          ? {
              ...row,
              ...updated,
              company_name: updated.company_name ?? row.company_name,
              agent_nom: updated.agent_nom ?? row.agent_nom,
              agent_prenom: updated.agent_prenom ?? row.agent_prenom,
              agent_matricule: updated.agent_matricule ?? row.agent_matricule
            }
          : row
      )
    );
    setDetailMember((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
    toast('success', 'Adhérent mis à jour.');
  };

  const openEditMember = (member) => {
    setEditMember(member);
  };

  const rangeLabel = useMemo(() => {
    if (!totalMembers) return null;
    const from = page * ITEMS_PAGE + 1;
    const to = Math.min(totalMembers, (page + 1) * ITEMS_PAGE);
    return `${from}–${to}`;
  }, [page, totalMembers]);

  const hasActiveFilters = Boolean(debouncedSearch.trim() || companyFilter !== 'all');
  const isSearching = searchTerm.trim() !== debouncedSearch.trim();
  const countLabel = loading
    ? 'Chargement…'
    : `${totalMembers} adhérent${totalMembers > 1 ? 's' : ''} actif${totalMembers > 1 ? 's' : ''}`;

  const listHead = (
    <div className="recv-browse-list__head" aria-hidden="true">
      <span className="recv-browse-list__cell recv-browse-list__cell--lead">Adhérent</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--profile">Profil</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--company">Société</span>
      <span className="recv-browse-list__cell recv-browse-list__cell--action">Action</span>
    </div>
  );

  const renderPagination = (position = 'bottom') => {
    if (loading || totalMembers <= 0) return null;
    const multiPage = totalPages > 1;
    return (
      <div
        className={`recv-journal-pagination recv-browse-pagination${position === 'top' ? ' mb-3' : ' mt-0 border-top'}`}
        aria-label="Pagination des adhérents"
      >
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page <= 0 || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Précédent
        </Button>
        <div className="recv-journal-pagination__info">
          Page <strong>{page + 1}</strong> / {totalPages}
          {' · '}
          <strong>{totalMembers}</strong> adhérent{totalMembers > 1 ? 's' : ''}
          {rangeLabel ? (
            <>
              {' '}
              (lignes {rangeLabel})
            </>
          ) : null}
          {!multiPage ? (
            <span className="d-block small mt-1">Tous les résultats sont sur cette page.</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page >= totalPages - 1 || loading}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Suivant →
        </Button>
      </div>
    );
  };

  return (
    <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--browse recv-panel-card--browse-members">
      <Card.Header className="recv-browse-panel__header border-bottom-0 pb-0">
        <div className="recv-browse-panel__hero">
          <div className="recv-browse-panel__intro">
            <div className="recv-browse-panel__eyebrow">Annuaire SMIE</div>
            <div className="recv-browse-panel__title">Adhérents actifs</div>
            <p className="recv-browse-panel__desc mb-0">
              Recherche par nom, prénom ou matricule — {countLabel}.
            </p>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="recv-browse-panel__body pt-0">
        <div className="recv-browse-toolbar">
          <BrowseSearchBar
            id="recv-members-search"
            label="Rechercher un adhérent"
            placeholder="Nom, prénom, matricule…"
            value={searchTerm}
            onChange={setSearchTerm}
            searching={isSearching}
            hint={
              isSearching
                ? 'Recherche en cours…'
                : hasActiveFilters
                  ? `${totalMembers} adhérent${totalMembers > 1 ? 's' : ''} correspondent aux critères.`
                  : 'Tapez un nom, un prénom ou un matricule SMIE. Utilisez « Détail » pour ouvrir la fiche.'
            }
          />
          <div className="recv-browse-toolbar__filters">
            <Form.Label className="recv-browse-toolbar__filter-label" htmlFor="recv-members-company">
              Société
            </Form.Label>
            <Form.Select
              id="recv-members-company"
              value={companyFilter}
              onChange={(ev) => setCompanyFilter(ev.target.value)}
              aria-label="Filtrer par société"
            >
              <option value="all">Toutes les sociétés</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="success"
              className="recv-browse-toolbar__cta text-nowrap"
              onClick={() => setShowHouseholdModal(true)}
            >
              + Nouveau foyer
            </Button>
          </div>
        </div>

        {selectedCompany ? (
          <div className="recv-browse-active-filter mt-2">
            Filtre société : <strong>{selectedCompany.name}</strong>
            <Button
              variant="link"
              size="sm"
              className="recv-browse-active-filter__clear"
              onClick={() => setCompanyFilter('all')}
            >
              Tout afficher
            </Button>
          </div>
        ) : null}

        {loading ? (
          <ReceptionLoadingBlock label="Chargement des adhérents…" />
        ) : members.length === 0 ? (
          <ReceptionEmptyState
            title="Aucun résultat"
            description="Affinez la recherche ou changez le filtre société."
            action={
              hasActiveFilters ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setCompanyFilter('all');
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            {renderPagination('top')}
            <div className="recv-browse-list-wrap recv-browse-list-wrap--members border rounded bg-white">
              <div className="recv-browse-list" role="list" aria-label="Liste des adhérents">
                {listHead}
                <MemberListRows
                  rows={members}
                  memberTypeLabels={memberTypeLabels}
                  onViewDetail={setDetailMember}
                />
              </div>
            </div>
            {renderPagination('bottom')}
          </>
        )}
      </Card.Body>

      <HouseholdRegistrationModal
        show={showHouseholdModal}
        onHide={() => setShowHouseholdModal(false)}
        companies={companies}
        toast={toast}
        onCreated={() => {
          setShowHouseholdModal(false);
          setPage(0);
          setReloadNonce((n) => n + 1);
        }}
      />

      <ReceptionMemberDetailModal
        show={!!detailMember}
        member={detailMember}
        memberTypeLabels={memberTypeLabels}
        onHide={() => setDetailMember(null)}
        onEdit={openEditMember}
      />

      <ReceptionMemberEditModal
        show={!!editMember}
        member={editMember}
        memberTypeLabels={memberTypeLabels}
        onHide={() => setEditMember(null)}
        onSaved={handleMemberUpdated}
      />
    </Card>
  );
}
